import React, { useEffect, useState, useCallback } from "react";
import { AlertCircle, Database } from "lucide-react";
import { api, formatINR, formatNum } from "@/lib/api";
import { logError } from "@/lib/logger";

const Chip = ({ label, value, accent = false, onClick, testId, title }) => {
  const cls = accent
    ? "border-[#B23A48]/40 bg-[#B23A48]/10 text-[#B23A48] hover:bg-[#B23A48]/20"
    : "border-border bg-secondary/40 text-muted-foreground hover:bg-secondary";
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={!onClick}
      title={title}
      className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border text-[11px] tabular transition-colors ${cls} disabled:cursor-default disabled:hover:bg-transparent`}
      data-testid={testId}
    >
      <span>{label}</span>
      <span className="font-semibold">{value}</span>
    </button>
  );
};

const _ago = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  const diff = (Date.now() - d.getTime()) / 1000;
  if (diff < 60) return `${Math.floor(diff)}s ago`;
  if (diff < 3600) return `${Math.floor(diff / 60)} min ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)} h ago`;
  return `${Math.floor(diff / 86400)} d ago`;
};

const useHygiene = () => {
  const [d, setD] = useState(null);
  const refresh = useCallback(async () => {
    try {
      const res = await api.dataHygiene();
      setD(res);
    } catch (e) {
      logError(e);
    }
  }, []);
  useEffect(() => {
    refresh();
    const t = setInterval(refresh, 60_000);
    return () => clearInterval(t);
  }, [refresh]);
  return d;
};

// Compact orphan chip used directly under the "Booking, Revenue & Collection
// by Vertical" chart. Clickable → opens the Collection drill-down restricted
// to payments whose payer has no vertical assignment.
export const OrphanPaymentsChip = ({ onClick }) => {
  const d = useHygiene();
  if (!d) return null;
  const { count, amount } = d.orphan_payments || {};
  if (!count) return null;
  return (
    <div className="mt-3 flex justify-end" data-testid="orphan-chip-row">
      <Chip
        label={`Orphan payments (${formatNum(count)}):`}
        value={formatINR(amount)}
        accent
        onClick={onClick}
        testId="orphan-payments-chip"
      />
    </div>
  );
};

// Full data-hygiene strip rendered at the bottom of the dashboard.
export const DataHygienePanel = ({ onOrphanClick }) => {
  const d = useHygiene();
  if (!d) return null;
  const orph = d.orphan_payments || {};
  const sync = d.last_sync || {};
  return (
    <div
      className="card-surface p-3 sm:p-4 flex flex-wrap items-center gap-2 sm:gap-3"
      data-testid="data-hygiene-panel"
    >
      <div className="flex items-center gap-2 shrink-0 pr-2 mr-1 border-r border-border">
        <Database className="w-3.5 h-3.5 text-muted-foreground" />
        <span className="text-[11px] uppercase tracking-widest font-semibold text-muted-foreground">
          Data hygiene
        </span>
      </div>

      <Chip
        label="Orphan payments"
        value={`${formatNum(orph.count || 0)} · ${formatINR(orph.amount || 0)}`}
        accent={!!orph.count}
        onClick={orph.count ? onOrphanClick : undefined}
        testId="hygiene-orphan-chip"
      />

      <Chip
        label="Students missing cohort"
        value={formatNum(d.students_missing_cohort || 0)}
        accent={(d.students_missing_cohort || 0) > 0}
        testId="hygiene-students-no-cohort"
      />

      <Chip
        label="Students missing vertical"
        value={formatNum(d.students_missing_vertical || 0)}
        accent={(d.students_missing_vertical || 0) > 0}
        testId="hygiene-students-no-vertical"
      />

      <Chip
        label="Partnerships missing dates"
        value={formatNum(d.partnerships_missing_dates || 0)}
        accent={(d.partnerships_missing_dates || 0) > 0}
        testId="hygiene-partnerships-no-dates"
      />

      <Chip
        label="Partnerships missing vertical"
        value={formatNum(d.partnerships_missing_vertical || 0)}
        accent={(d.partnerships_missing_vertical || 0) > 0}
        testId="hygiene-partnerships-no-vertical"
      />

      <Chip
        label="Drop-out data issues"
        value={formatNum((d.dropout_missing_date || 0) + (d.dropout_inconsistent || 0))}
        accent={((d.dropout_missing_date || 0) + (d.dropout_inconsistent || 0)) > 0}
        testId="hygiene-dropout-issues"
        title={
          `${d.dropout_missing_date || 0} flagged drop-out without a date · ` +
          `${d.dropout_inconsistent || 0} have a date but the flag is off`
        }
      />

      <div className="ml-auto inline-flex items-center gap-2 text-[11px] text-muted-foreground tabular">
        <AlertCircle className="w-3 h-3" />
        <span>
          Last sync: <span className="text-foreground font-semibold">{_ago(sync.finished_at)}</span>
        </span>
      </div>
    </div>
  );
};
